import { exactTermSource } from '../components/viewer/termMarks';
/**
 * compareExport — download a side-by-side keyword comparison as a
 * self-contained HTML file (opens offline, keyword highlighted) or as Excel.
 *
 * Requirement: "Export/download label compare side by side in excel or HTML
 * (for when ppl don't have wifi). Export: includes highlighting of text."
 */

import { USER_HIGHLIGHT_COLOR, type ColoredTerm } from './highlightColors';
import { markPhrasesInHtml } from './phraseMatch';
import type { KeywordMatchHighlight, KeywordSearchResultItem } from '../types';
import { recolorHighlightedHtml } from '../components/SplContentRenderer';
import { FORMAT_BOX, fitBox, toDataUri } from './exportImageLayout';
import type { EmbeddedPage } from './pdfPageImages';
import { markBoxes, marksByPage, MARK_OPACITY, type PageMark } from './pageMarks';
import { beginDeliver, finishExport, type ExportRunOptions } from './exportProgress';

/** Shown under the heading of a block whose layout HTML failed verification. */
export const UNVERIFIED_NOTE = 'OCR text not fully verified.';

export interface ExportBlock {
  heading?: string | null | undefined;
  /** Layout HTML from a page that failed verification (needs_review). */
  unverified?: boolean;
  /** Plain text (escaped + keyword-highlighted at render). */
  text?: string;
  /** Pre-formatted + sanitized + highlighted HTML (SPL formatting). Preferred
   *  over ``text`` when present so the export keeps the on-screen formatting. */
  html?: string;
  /** JP bilingual: the Japanese-original heading + content HTML, so the hosted
   *  snapshot can offer an EN/日本語 toggle. ``html``/``heading`` hold the English
   *  (default, what the static .html/.xls download renders); these hold the
   *  Japanese counterpart. Absent for non-JP blocks. */
  heading_ja?: string | null | undefined;
  html_ja?: string;
}
/** One TOC row carried into a snapshot. Carries the FULL live-viewer hierarchy
 *  (level / parent_section_id / group_key / is_group_header / page_start) so the
 *  snapshot Contents can render the SAME indented, collapsible tree as the live
 *  TOCSidebar instead of a flat list. ``page_num`` is the legacy flat field kept
 *  so snapshots exported before the hierarchy change still render (flat). */
export interface ExportTocEntry {
  section_number: string | null;
  title: string;
  /** Legacy flat click target (older snapshots). New snapshots use page_start. */
  page_num?: number | null;
  // ── hierarchy (present on snapshots exported after the unification) ──
  id?: number;
  level?: number;
  parent_section_number?: string | null;
  parent_section_id?: number | null;
  page_start?: number | null;
  page_end?: number | null;
  section_type?: string | null;
  group_key?: string | null;
  group_label?: string | null;
  is_group_header?: boolean;
}
/** A regulatory (PDF) drug's pages + TOC, so a public snapshot can render the
 *  actual document pages. image_url is a public GCS URL (loads without auth). */
export interface ExportDrugPdf {
  toc: ExportTocEntry[];
  pages: { page_num: number; image_url: string; width: number | null; height: number | null }[];
  /** Keyword match boxes (from the page text layer) so the snapshot can color
   *  the matched words on the page image. Coords are 0-1 fractions of the page;
   *  color is the per-row highlight color. */
  marks?: { page: number; x0: number; y0: number; x1: number; y1: number; color: string }[];
  /** Blocks hold only the selected sections' text; file exports drop the page images (they show neighbouring sections). Snapshot still renders `pages`. */
  sectionScoped?: boolean;
}
export interface ExportDrug {
  name: string;
  subtitle?: string | undefined;
  note?: string | undefined; // e.g. "Matched excerpts — open the document for the full PDF"
  blocks: ExportBlock[];
  /** Present for PDF-label drugs → snapshot renders the zoomable page viewer. */
  pdf?: ExportDrugPdf;
  /** True when the drug record could not be loaded (getDrug failed → likely a
   *  deleted drug_id from a stale keyword posting) and no authoritative content
   *  (PDF pages) was fetched — any `blocks` are stale search-hit excerpts only.
   *  The Share flow drops these so a deleted drug is never frozen into a public
   *  snapshot; exports still render them (with the truthful note). */
  unreachable?: boolean;
}

function escapeHtml(s: string): string {
  return s.replace(/[&<>"]/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;' }[c] as string));
}

export function stripTags(s: string | null | undefined): string {
  return (s ?? '').replace(/<[^>]+>/g, ' ').replace(/\s+\n/g, '\n');
}

/** Wildcard-aware highlight regex (same rules as the document viewers). */
function buildTermRegex(term?: string | null): RegExp | null {
  if (!term) return null;
  const tokens = term.trim().split(/\s+/).filter(Boolean);
  if (!tokens.length) return null;
  const esc = (x: string) => x.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
  const parts = tokens.map((t) => t.split('*').map(esc).join('\\w*'));
  try {
    return new RegExp(parts.join('\\s+'), 'gi');
  } catch {
    return null;
  }
}

/** Escape, then wrap keyword matches in <mark> (the highlight survives offline). */
function highlight(text: string, re: RegExp | null): string {
  const safe = escapeHtml(text);
  if (!re) return safe;
  re.lastIndex = 0;
  return safe.replace(re, (m) => `<mark>${m}</mark>`);
}

/** Multi-row variant: wrap each term's matches in a per-row colored <mark>.
 *  Single pass over the raw text (collect → resolve overlaps earliest/longest →
 *  emit with escaping) so the inline color survives offline and no mark is
 *  re-matched. */
function highlightColored(text: string, terms: ColoredTerm[], englishExact = false): string {
  if (!terms.length) return escapeHtml(text);
  const ranges: { start: number; end: number; color: string }[] = [];
  for (const { term, color } of terms) {
    const source = englishExact ? exactTermSource(term) : null;
    const re = source ? new RegExp(source, 'gi') : buildTermRegex(term);
    if (!re) continue;
    re.lastIndex = 0;
    let m: RegExpExecArray | null;
    while ((m = re.exec(text)) !== null) {
      if (m[0].length === 0) { re.lastIndex++; continue; }
      ranges.push({ start: m.index, end: m.index + m[0].length, color });
    }
  }
  if (!ranges.length) return escapeHtml(text);
  ranges.sort((a, b) => a.start - b.start || (b.end - b.start) - (a.end - a.start));
  let out = '';
  let pos = 0;
  for (const r of ranges) {
    if (r.start < pos) continue; // overlap → keep earliest/longest already emitted
    if (r.start > pos) out += escapeHtml(text.slice(pos, r.start));
    out += `<mark style="background:${r.color}">${escapeHtml(text.slice(r.start, r.end))}</mark>`;
    pos = r.end;
  }
  if (pos < text.length) out += escapeHtml(text.slice(pos));
  return out;
}

/** Static legend row (one swatch per entry) for the export/snapshot HTML. */
function legendHtml(terms: ColoredTerm[]): string {
  if (!terms.length) return '';
  const items = terms
    .map(
      (t) =>
        `<span class="lg-item"><span class="lg-sw" style="background:${t.color}"></span>${escapeHtml(t.term)}</span>`,
    )
    .join('');
  return `<div class="legend"><span class="lg-label">Matches:</span>${items}</div>`;
}

export function slug(s: string): string {
  return (s || 'export').replace(/[^a-z0-9]+/gi, '-').replace(/^-+|-+$/g, '').slice(0, 40) || 'export';
}

// Scoped CSS (under .kwcmp) so the table renders correctly both as a
// standalone file AND embedded in the snapshot page without leaking styles.
const KWCMP_CSS = `
  .kwcmp{font-family:-apple-system,Segoe UI,Roboto,Arial,sans-serif;color:#1e293b;}
  .kwcmp h1{font-size:18px;margin:0 0 4px;} .kwcmp .sub{color:#64748b;font-size:13px;margin:0 0 16px;}
  .kwcmp table{border-collapse:collapse;width:100%;table-layout:fixed;}
  .kwcmp th,.kwcmp td{border:1px solid #e2e8f0;vertical-align:top;padding:12px;font-size:13px;line-height:1.5;}
  .kwcmp th{background:#f8fafc;text-align:left;}
  .kwcmp .dn{font-weight:700;} .kwcmp .ds{color:#64748b;font-size:11px;font-weight:400;}
  .kwcmp h4{font-size:12px;margin:14px 0 4px;color:#0f172a;}
  .kwcmp .bt{margin:0 0 8px;white-space:pre-wrap;word-wrap:break-word;}
  .kwcmp .bh{margin:0 0 8px;}
  .kwcmp .unv{font-size:11px;font-style:italic;color:#6b7280;margin:0 0 4px;}
  .kwcmp .bh p{margin:6px 0;} .kwcmp .bh ul,.kwcmp .bh ol{margin:6px 0;padding-left:20px;}
  .kwcmp .bh li{margin:2px 0;} .kwcmp .bh strong,.kwcmp .bh b{font-weight:600;}
  .kwcmp .bh a{color:#14746D;text-decoration:underline;}
  .kwcmp .bh table{border-collapse:collapse;margin:8px 0;width:auto;}
  .kwcmp .bh td,.kwcmp .bh th{border:1px solid #cbd5e1;padding:4px 6px;font-size:12px;}
  .kwcmp mark{background:#fde047;padding:0 1px;border-radius:2px;}
  .kwcmp .note{font-size:11px;color:#92400e;background:#fffbeb;border:1px solid #fde68a;padding:6px;border-radius:4px;margin-bottom:8px;}
  .kwcmp .empty{color:#94a3b8;}
  .kwcmp .legend{display:flex;flex-wrap:wrap;gap:6px 14px;align-items:center;margin:0 0 14px;font-size:12px;}
  .kwcmp .legend .lg-label{color:#94a3b8;font-weight:500;}
  .kwcmp .legend .lg-item{display:inline-flex;align-items:center;gap:5px;color:#475569;}
  .kwcmp .legend .lg-sw{display:inline-block;width:12px;height:12px;border-radius:2px;border:1px solid #cbd5e1;}
`;

/** Escape + keyword-highlight a plain-text block: per-row colors for a
 *  multi-row query, else the single default-yellow keyword <mark>. */
export function highlightBlockText(text: string, keyword: string, coloredTerms?: ColoredTerm[], englishExact = false): string {
  if (englishExact) {
    const terms = coloredTerms?.length ? coloredTerms : keyword ? [{ term: keyword, color: '' }] : [];
    return highlightColored(text, terms, true);
  }
  return coloredTerms && coloredTerms.length > 1
    ? highlightColored(text, coloredTerms)
    : highlight(text, buildTermRegex(keyword));
}

/** A block's content as highlighted HTML: its pre-rendered HTML, or its plain
 *  text keyword-highlighted with line breaks kept (for Word, Excel, manual marks). */
export function exportBlockHtml(block: ExportBlock, keyword: string, coloredTerms?: ColoredTerm[]): string {
  return block.html ?? highlightBlockText(block.text ?? '', keyword, coloredTerms).replace(/\n/g, '<br>');
}

/** Marks the user's manual highlights in export HTML (tells them apart from keyword marks). */
export const USER_HIGHLIGHT_ATTR = 'data-user-highlight';

/** One drug's export with the user's manual highlights marked in USER_HIGHLIGHT_COLOR.
 *  Only blocks containing a highlighted phrase change; a plain-text block becomes
 *  highlighted HTML (keyword highlights kept, line breaks → <br>). */
export function withUserHighlights(
  drug: ExportDrug,
  phrases: string[],
  keyword: string,
  coloredTerms?: ColoredTerm[],
): ExportDrug {
  if (!phrases.length) return drug;
  const blocks = drug.blocks.map((block) => {
    const marked = markPhrasesInHtml(exportBlockHtml(block, keyword, coloredTerms), phrases, USER_HIGHLIGHT_COLOR, USER_HIGHLIGHT_ATTR);
    if (marked === null) return block;
    const { text: _plain, ...rest } = block;
    return { ...rest, html: marked };
  });
  return { ...drug, blocks };
}

/** Legend entries for an export: the per-row keyword colors (multi-row queries)
 *  plus "Your highlights" when a manual highlight made it into the export. */
export function exportLegend(drugs: ExportDrug[], coloredTerms?: ColoredTerm[]): ColoredTerm[] {
  const rows = coloredTerms && coloredTerms.length > 1 ? coloredTerms : [];
  const hasUserHighlights = drugs.some((d) => d.blocks.some((b) => b.html?.includes(USER_HIGHLIGHT_ATTR)));
  return hasUserHighlights ? [...rows, { term: 'Your highlights', color: USER_HIGHLIGHT_COLOR }] : rows;
}

// Side-scrolling layout: the table never squeezes a drug
// column below COMPARE_COL_MIN_PX — with more drugs than fit, the page scrolls
// sideways and the drug names stay pinned.
const COMPARE_COL_MIN_PX = 380;
const KWCMP_SCROLL_CSS = `
  .kwcmp th,.kwcmp td{overflow-wrap:anywhere;}
  .kwcmp thead th{position:sticky;top:0;z-index:1;}
  .kwcmp .cell{overflow-x:auto;}
`;

// Page images under a drug's text. Only emitted when there are images, so an
// export without them is byte-identical to the pre-image output.
const KWCMP_PAGES_CSS = `
  .kwcmp .pages h4{margin-top:18px;}
  .kwcmp figure{margin:0 0 14px;}
  .kwcmp figcaption{font-size:11px;font-weight:600;color:#64748b;margin:0 0 4px;}
  .kwcmp figure img{display:block;max-width:100%;height:auto;border:1px solid #e2e8f0;}
  .kwcmp .pg-marked{position:relative;display:block;max-width:100%;}
  .kwcmp .pg-marked img{width:100%;}
  .kwcmp .pg-mark{position:absolute;mix-blend-mode:multiply;opacity:${MARK_OPACITY};pointer-events:none;}
`;

/** One page image as the HTML export carries it. `src` is a data: URI in the
 *  downloaded file (renders offline) or the page's remote URL in the server
 *  job's online preview (small, lazy-loaded) — one builder serves both. */
export interface CompareHtmlImage {
  src: string;
  pageNum: number;
  width?: number | null;
  height?: number | null;
  /** Keyword boxes drawn over the image (remote images only; embedded ones
   *  have them painted in). */
  marks?: PageMark[];
}

export interface CompareHtmlOptions {
  /** Per drug, aligned to `drugs`; undefined for a drug with no page images. */
  images?: (CompareHtmlImage[] | undefined)[];
  /** Audit line appended to the summary (see embedPagesForExport). */
  imageNote?: string;
  /** Keep the text of a drug that also has page images. Default true. */
  includePageText?: boolean;
}

/** Fetched pages → base64 data URIs, for the downloadable (offline) file. */
export function embeddedPagesAsHtmlImages(images: (EmbeddedPage[] | undefined)[]): (CompareHtmlImage[] | undefined)[] {
  return images.map((pages) => pages?.map((p) => ({ src: toDataUri(p), pageNum: p.pageNum, width: p.width, height: p.height })));
}

/** Each PDF drug's pages as remote <img> sources, for an online preview. A
 *  remote image can't have the keyword boxes painted in, so they go on top as
 *  an overlay instead (the embedded images have them burned in). */
export function remotePageImages(drugs: ExportDrug[]): (CompareHtmlImage[] | undefined)[] {
  return drugs.map((d) => {
    if (!d.pdf || d.pdf.sectionScoped) return undefined;
    const marks = marksByPage(d.pdf.marks);
    return d.pdf.pages.map((p) => {
      const pageMarks = marks.get(p.page_num);
      return { src: p.image_url, pageNum: p.page_num, width: p.width, height: p.height, ...(pageMarks ? { marks: pageMarks } : {}) };
    });
  });
}

/** Keyword boxes over a remote page image, positioned in % of the page. */
function markOverlayHtml(marks: PageMark[] | undefined): string {
  return markBoxes(marks, 10_000, 10_000)
    .map((b) => `<span class="pg-mark" style="left:${b.x / 100}%;top:${b.y / 100}%;width:${b.width / 100}%;height:${b.height / 100}%;background:${b.color}"></span>`)
    .join('');
}

function pagesHtml(pages: CompareHtmlImage[]): string {
  const figures = pages
    .map((p) => {
      const { width, height } = fitBox(p, FORMAT_BOX.html);
      const label = `Page ${p.pageNum + 1}`;
      const img = `<img src="${escapeHtml(p.src)}" width="${width}" height="${height}" alt="${label}" loading="lazy">`;
      const body = p.marks?.length
        ? `<span class="pg-marked" style="width:${width}px">${img}${markOverlayHtml(p.marks)}</span>`
        : img;
      return `<figure><figcaption>${label}</figcaption>${body}</figure>`;
    })
    .join('');
  return `<div class="pages"><h4>Document pages</h4>${figures}</div>`;
}

/** Self-contained, embeddable comparison HTML (scoped <style> + table): one
 *  column per drug, each PDF drug's page images under its text when given. */
export function buildCompareTableHtml(
  drugs: ExportDrug[],
  keyword: string,
  section?: string,
  coloredTerms?: ColoredTerm[],
  opts: CompareHtmlOptions = {},
): string {
  const includePageText = opts.includePageText ?? true;
  const hasImages = !!opts.images?.some((pages) => pages && pages.length);
  const multi = !!coloredTerms && coloredTerms.length > 1;
  const colW = `${100 / Math.max(1, drugs.length)}%`;
  const headers = drugs
    .map(
      (d) =>
        `<th style="width:${colW}"><div class="dn">${escapeHtml(d.name)}</div>${
          d.subtitle ? `<div class="ds">${escapeHtml(d.subtitle)}</div>` : ''
        }</th>`,
    )
    .join('');
  const cells = drugs
    .map((d, i) => {
      const note = d.note ? `<div class="note">${escapeHtml(d.note)}</div>` : '';
      const pages = opts.images?.[i];
      const pageImages = pages && pages.length ? pagesHtml(pages) : '';
      const text = pageImages && !includePageText ? '' : d.blocks.length
        ? d.blocks
            .map((b) => {
              const head = (b.heading ? `<h4>${escapeHtml(b.heading)}</h4>` : '')
                + (b.unverified ? `<div class="unv">${escapeHtml(UNVERIFIED_NOTE)}</div>` : '');
              // Prefer pre-formatted HTML (SPL formatting + highlights already
              // applied + sanitized upstream); else escape+highlight plain text.
              const content = b.html
                ? `<div class="bh">${b.html}</div>`
                : `<div class="bt">${highlightBlockText(b.text ?? '', keyword, coloredTerms)}</div>`;
              return head + content;
            })
            .join('')
        : '<div class="empty">No content for this section.</div>';
      const body = text + pageImages;
      return `<td><div class="cell">${note}${body}</div></td>`;
    })
    .join('');
  const subHighlight = multi
    ? `${coloredTerms!.length} query rows (color-coded)`
    : `&ldquo;${escapeHtml(keyword)}&rdquo;`;
  const table = `<table style="min-width:${drugs.length * COMPARE_COL_MIN_PX}px">`;
  return `<div class="kwcmp"><style>${KWCMP_CSS}${KWCMP_SCROLL_CSS}${hasImages ? KWCMP_PAGES_CSS : ''}</style>
<h1>Keyword comparison — &ldquo;${escapeHtml(keyword)}&rdquo;</h1>
<p class="sub">${drugs.length} drug(s)${section ? ` · section ${escapeHtml(section)}` : ''} · highlighted: ${subHighlight}${opts.imageNote ? ` · ${escapeHtml(opts.imageNote)}` : ''}</p>
${legendHtml(exportLegend(drugs, coloredTerms))}
${table}<thead><tr>${headers}</tr></thead><tbody><tr>${cells}</tr></tbody></table></div>`;
}

function fullDoc(inner: string, keyword: string): string {
  return `<!doctype html><html lang="en"><head><meta charset="utf-8"><title>Keyword comparison — ${escapeHtml(
    keyword,
  )}</title></head><body style="margin:24px">${inner}</body></html>`;
}

/** A built export file, before anything is done with it: the browser hands it
 *  to triggerDownload, the server export worker uploads it to GCS. Splitting
 *  build from delivery is what lets both run the same builder. */
export interface RenderedExport {
  data: ArrayBuffer | string;
  filename: string;
  mime: string;
  /** Page images embedded, and their bytes (timings telemetry). */
  embedded: number;
  mediaBytes: number;
  imageNote: string | undefined;
}

/** Deliver a rendered export in the browser, on the shared progress scale. */
export function deliverExport(rendered: RenderedExport, options: ExportRunOptions = {}): void {
  beginDeliver(options);
  triggerDownload(rendered.data, rendered.filename, rendered.mime);
  finishExport(options);
}

export function triggerDownload(content: BlobPart, filename: string, mime: string) {
  const blob = new Blob([content], { type: mime });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
  URL.revokeObjectURL(url);
}

/** The downloadable HTML. PDF drugs' page images are embedded as base64 so the
 *  file still renders offline (the reason this export exists). */
export async function renderCompareHtml(
  drugs: ExportDrug[],
  keyword: string,
  section?: string,
  coloredTerms?: ColoredTerm[],
  options: ExportRunOptions = {},
): Promise<RenderedExport> {
  let embedded = 0, mediaBytes = 0;
  let imageNote: string | undefined;
  // Lazy: the fetch/encode pipeline (and its worker) only loads on export.
  const { embedPagesStage } = await import('./pdfPageImages');
  const out = await embedPagesStage(drugs, 'html', options);
  ({ embedded, mediaBytes } = out);
  imageNote = out.note;
  const opts: CompareHtmlOptions = {
    ...(out.images ? { images: embeddedPagesAsHtmlImages(out.images) } : {}),
    ...(out.note ? { imageNote: out.note } : {}),
    ...(options.includePageText === undefined ? {} : { includePageText: options.includePageText }),
  };
  return {
    data: fullDoc(buildCompareTableHtml(drugs, keyword, section, coloredTerms, opts), keyword),
    filename: `compare-${slug(keyword)}.html`,
    mime: 'text/html;charset=utf-8',
    embedded,
    mediaBytes,
    imageNote,
  };
}

/** The server job's online preview: the same comparison with REMOTE page image
 *  URLs (small and lazy-loaded) instead of embedded base64. For Word and Excel
 *  it is a web preview of the same content, not the file's exact layout. */
export function renderComparePreviewHtml(
  drugs: ExportDrug[],
  keyword: string,
  section?: string,
  coloredTerms?: ColoredTerm[],
  opts: { imageNote?: string; includePageText?: boolean } = {},
): string {
  return fullDoc(buildCompareTableHtml(drugs, keyword, section, coloredTerms, { ...opts, images: remotePageImages(drugs) }), keyword);
}

/** The downloadable HTML. PDF drugs' page images are embedded as base64 so the
 *  file still renders offline (the reason this export exists). */
export async function downloadCompareHtml(
  drugs: ExportDrug[],
  keyword: string,
  section?: string,
  coloredTerms?: ColoredTerm[],
  options: ExportRunOptions = {},
): Promise<void> {
  deliverExport(await renderCompareHtml(drugs, keyword, section, coloredTerms, options), options);
}

/** Excel ignores <mark> backgrounds but DOES honour inline `background-color` on a
 *  <span>. Convert every highlight mark (colored or default-yellow) to an inline
 *  span so the row colors actually render in Excel. Applied ONLY to the .xls path
 *  — the .html / live / snapshot views keep <mark> (renders fine in browsers). */
function marksToInlineSpans(html: string): string {
  return html
    .replace(/<mark\b[^>]*?\sstyle="background:([^"]+)"[^>]*>/gi,
             '<span style="background-color:$1;padding:0 1px">')
    .replace(/<mark(\s[^>]*)?>/gi, '<span style="background-color:#fde047;padding:0 1px">')
    .replace(/<\/mark>/gi, '</span>');
}

/** Excel opens an HTML table; same markup so the side-by-side layout carries over.
 *  Highlight marks are converted to inline-background spans so the row colors show
 *  in Excel (it doesn't render <mark> backgrounds). Never carries page images:
 *  Excel's HTML import supports neither data: URIs nor reliable remote images —
 *  the .xlsx export is the one with pages. */
export function downloadCompareXls(drugs: ExportDrug[], keyword: string, section?: string, coloredTerms?: ColoredTerm[], options: ExportRunOptions = {}) {
  options.timer?.start('build');
  const html = marksToInlineSpans(fullDoc(buildCompareTableHtml(drugs, keyword, section, coloredTerms), keyword));
  beginDeliver(options);
  triggerDownload(html, `compare-${slug(keyword)}.xls`, 'application/vnd.ms-excel');
  finishExport(options);
}

// ── Results-list HTML export (highlighted) ───────────────────────────────────
// CSV exports can't carry highlight colors; this is the highlighted offline
// export from the RESULTS page — each drug + its matched excerpts, with the same
// per-row colors as the results/compare views (recolored from the backend
// headline) + a legend.
const KWRES_CSS = `
  .kwres{font-family:-apple-system,Segoe UI,Roboto,Arial,sans-serif;color:#1e293b;max-width:920px;margin:0 auto;}
  .kwres h1{font-size:18px;margin:0 0 4px;} .kwres .sub{color:#64748b;font-size:13px;margin:0 0 16px;}
  .kwres .card{border:1px solid #e2e8f0;border-radius:8px;padding:12px 14px;margin:0 0 10px;}
  .kwres .name{font-weight:700;font-size:14px;} .kwres .mc{color:#64748b;font-weight:400;font-size:12px;}
  .kwres .dsub{color:#64748b;font-size:12px;margin:2px 0 8px;}
  .kwres .hl{margin:6px 0;padding-left:10px;border-left:2px solid #e2e8f0;}
  .kwres .sec{color:#475569;font-size:11px;font-weight:600;margin-bottom:2px;}
  .kwres .hd{font-size:13px;line-height:1.5;} .kwres mark{background:#fde047;padding:0 1px;border-radius:2px;}
  .kwres .legend{display:flex;flex-wrap:wrap;gap:6px 14px;align-items:center;margin:0 0 16px;font-size:12px;}
  .kwres .legend .lg-label{color:#94a3b8;font-weight:500;}
  .kwres .legend .lg-item{display:inline-flex;align-items:center;gap:5px;color:#475569;}
  .kwres .legend .lg-sw{display:inline-block;width:12px;height:12px;border-radius:2px;border:1px solid #cbd5e1;}
`;

/** A search result's display name + "generic · manufacturer · region" line. */
export function resultTitle(r: KeywordSearchResultItem): { name: string; subtitle: string } {
  const name = r.brand_name || r.generic_name || `Drug ${r.drug_id}`;
  const subtitle = [r.brand_name && r.generic_name ? r.generic_name : null, r.manufacturer, r.region]
    .filter(Boolean)
    .join(' · ');
  return { name, subtitle };
}

export function matchCountLabel(n: number): string {
  return `${n} ${n === 1 ? 'match' : 'matches'}`;
}

/** "5.1  Warnings" for a matched excerpt (empty when the section is unknown). */
export function excerptSectionLabel(h: KeywordMatchHighlight): string {
  return [h.section_number, h.section_title].filter(Boolean).join('  ');
}

/** A matched excerpt's HTML: the backend headline already carries <mark>s;
 *  recolor them per query row for a multi-row query. */
export function excerptHtml(h: KeywordMatchHighlight, coloredTerms?: ColoredTerm[]): string {
  if (!coloredTerms || coloredTerms.length < 2) return h.headline ?? '';
  return recolorHighlightedHtml(h.headline ?? '', coloredTerms.map((c) => c.term), coloredTerms.map((c) => c.color));
}

export function exportResultsHtml(
  results: KeywordSearchResultItem[],
  keyword: string,
  coloredTerms?: ColoredTerm[],
): void {
  const multi = !!coloredTerms && coloredTerms.length > 1;
  const cards = results
    .map((r) => {
      const { name, subtitle } = resultTitle(r);
      const hls = (r.highlights ?? [])
        .map((h) => {
          const sec = excerptSectionLabel(h);
          return `<div class="hl">${sec ? `<div class="sec">${escapeHtml(sec)}</div>` : ''}<div class="hd">${excerptHtml(h, coloredTerms)}</div></div>`;
        })
        .join('');
      return `<div class="card"><div class="name">${escapeHtml(name)} <span class="mc">${matchCountLabel(r.total_matches)}</span></div>${
        subtitle ? `<div class="dsub">${escapeHtml(subtitle)}</div>` : ''
      }${hls}</div>`;
    })
    .join('');
  const body = `<div class="kwres"><style>${KWRES_CSS}</style>
<h1>Keyword results — &ldquo;${escapeHtml(keyword)}&rdquo;</h1>
<p class="sub">${results.length} drug(s)${multi ? ` · ${coloredTerms!.length} highlighted terms (color-coded by row)` : ''}</p>
${multi ? legendHtml(coloredTerms!) : ''}
${cards}</div>`;
  triggerDownload(fullDoc(body, keyword), `search-${slug(keyword)}.html`, 'text/html;charset=utf-8');
}
